import { FunctionComponent, RefObject, useRef, useEffect, useState } from "react";

import {
    FormControl,
    FormLabel,
    Input,
    FormErrorMessage,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Flex,
    Box,
    Button,
    Switch,
    Image,
    Icon,
} from "@chakra-ui/react";
import { IconPhotoOff } from "@tabler/icons-react";
import { FileContainer, Textarea } from "am-tax-fe-core";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import { Report, useCurrentUser, useUploadEngagementReportImageFile } from "../../api";
import { User } from "../../api";
import {
    useEngagementCreateReport,
    useEngagementUpdateReport,
    useProjectCreateReport,
    useProjectUpdateReport,
    useRemoveReport,
    useUploadEngagementReportFile,
    useUploadProjectReportFile,
    useUploadProjectReportImageFile,
} from "../../api";
import UploadButton from "../../components/UploadButton/UploadButton";

interface Props {
    engagementId: string;
    projectId?: string;
    report?: Report;
    focusAfterClosingRef?: RefObject<HTMLElement>;
    show: boolean;
    closeDialog: () => void;
}

type Inputs = {
    title: string;
    description: string;
    fileName: string;
    isPublished: boolean;
    imageFileName: string;
    imageFile?: FileContainer;
};

const EditReportDialog: FunctionComponent<Props> = ({ engagementId, projectId, report, show, closeDialog }: Props) => {
    const initialTitleRef = useRef<HTMLInputElement | null>(null);
    const currentUserQuery = useCurrentUser();
    const currentUser = currentUserQuery.data as User;

    const [selectedFileContainer, setSelectedFileContainer] = useState<FileContainer | undefined>();
    const [selectedImageFileContainer, setSelectedImageFileContainer] = useState<FileContainer | undefined>();

    const { mutateAsync: createEngagementReportAsync, isPending: createEngagementReportPending } = useEngagementCreateReport();
    const { mutateAsync: createProjectReportAsync, isPending: createProjecttReportPending } = useProjectCreateReport();
    const createReportAsync = projectId ? createProjectReportAsync : createEngagementReportAsync;
    const createReportPending = projectId ? createProjecttReportPending : createEngagementReportPending;

    const { mutateAsync: updateProjectReportAsync, isPending: updateProjectReportPending } = useProjectUpdateReport();
    const { mutateAsync: updateEngagementReportAsync, isPending: updateEngagementReportPending } = useEngagementUpdateReport();
    const { mutateAsync: removeReportAsync, isPending: removeReportPending } = useRemoveReport();
    const { mutateAsync: uploadEngagementReportFileAsync, isPending: uploadEngagementReportFilePending } = useUploadEngagementReportFile();
    const { mutateAsync: uploadProjectReportFileAsync, isPending: uploadProjectReportFilePending } = useUploadProjectReportFile();

    const { mutateAsync: uploadEngagementReportImageAsync, isPending: uploadEngagementReportImagePending } = useUploadEngagementReportImageFile();
    const { mutateAsync: uploadProjectReportImageAsync, isPending: uploadProjectReportImagePending } = useUploadProjectReportImageFile();

    const uploadReportFileAsync = projectId ? uploadProjectReportFileAsync : uploadEngagementReportFileAsync;
    const uploadReportFilePending = projectId ? uploadProjectReportFilePending : uploadEngagementReportFilePending;

    const uploadReportImageFileAsync = projectId ? uploadProjectReportImageAsync : uploadEngagementReportImageAsync;
    const uploadReportImageFilePending = projectId ? uploadProjectReportImagePending : uploadEngagementReportImagePending;

    const updateReportFilePending = projectId ? updateProjectReportPending : updateEngagementReportPending;

    const {
        register,
        handleSubmit,
        formState: { errors },
        resetField,
        setValue,
        setError,
        clearErrors,
        control,
    } = useForm<Inputs>();

    useEffect(() => {
        if (show) {
            if (report) {
                setValue("title", report.title);
                setValue("description", report.description || "");
                setValue("fileName", report.url);
                setValue("isPublished", report.isPublishedToClient);
                setValue("imageFileName", report.imageFileName || "");
                //                setValue("showOnDashboard", report.showOnDashboard);
            } else {
                setValue("isPublished", true);
            }
        }
    }, [show, setValue, report]);

    const handleClose = () => {
        closeDialog();
        resetField("title");
        resetField("description");
        resetField("fileName");
        resetField("isPublished");
        resetField("imageFileName");
        resetField("imageFile");
        clearErrors();
        setSelectedFileContainer(undefined);
        setSelectedImageFileContainer(undefined);
    };

    const handleRemoveReportClick = (rep: Report) => {
        removeReportAsync(
            { engagementId, ...rep },
            {
                onSuccess: () => {
                    handleClose();
                },
            },
        );
    };

    const updateReport = async (data: Inputs, report: Report) => {
        if (!report || !report.id) {
            return; // failed
        }

        const engagementArgs = {
            engagementId,
            ...report,
            title: data.title,
            description: data.description,
            url: data.fileName,
            isPublishedToClient: data.isPublished,
            imageFileName: data.imageFileName,
        };

        if (!projectId) {
            updateEngagementReportAsync(engagementArgs, {
                onSuccess: () => {
                    handleClose();
                },
            });
        } else {
            updateProjectReportAsync(
                { ...engagementArgs, projectId },
                {
                    onSuccess: () => {
                        handleClose();
                    },
                },
            );
        }

        //Upload report file
        if (selectedFileContainer != undefined || selectedFileContainer != null) {
            const uploadArgs = { engagementId, projectId: projectId || "", reportId: report.id, fileContainer: selectedFileContainer };
            await uploadReportFileAsync(uploadArgs);
        }

        //upload Image file
        if (selectedImageFileContainer != undefined || selectedImageFileContainer != null) {
            const imageUploadArgs = { engagementId, projectId: projectId || "", reportId: report.id, fileContainer: selectedImageFileContainer };
            await uploadReportImageFileAsync(imageUploadArgs);
        }
    };

    const createReport = async (data: Inputs) => {
        if (!selectedFileContainer) {
            return;
        }
        const emptyReport = {} as Report;
        const isPublished = currentUser.isInternal ? data.isPublished : true;
        const args = {
            ...emptyReport,
            engagementId,
            projectId: projectId as string,
            title: data.title,
            description: data.description,
            url: data.fileName,
            isPublishedToClient: isPublished,
            imageFileName: data.imageFileName,
            //            showOnDashboard: data.showOnDashboard,
        };
        const report = await createReportAsync(args);
        if (!report || !report.id) {
            return; // failed
        }

        try {
            //Upload report file
            const uploadArgs = { engagementId, projectId: projectId || "", reportId: report.id, fileContainer: selectedFileContainer };
            await uploadReportFileAsync(uploadArgs);

            //upload Image file
            const imageUploadArgs = { engagementId, projectId: projectId || "", reportId: report.id, fileContainer: selectedImageFileContainer };

            if (!errors.imageFile && imageUploadArgs.fileContainer) {
                // @ts-ignore
                await uploadReportImageFileAsync(imageUploadArgs);
            }
            handleClose();
        } catch (error) {
            // rollback report creation due to invalid report-file
            removeReportAsync(
                { engagementId, ...report },
                {
                    onSuccess: () => {
                        resetField("fileName");
                        setSelectedFileContainer(undefined);
                    },
                },
            );
        }
    };

    const onSubmit: SubmitHandler<Inputs> = data => {
        if (report) {
            updateReport(data, report);
        } else {
            createReport(data);
        }
    };

    // dev-note: deconstructing register to get hold of internal ref
    const { ref: titleRef, ...titleRest } = register("title", {
        required: "Please fill in a report title",
    });

    const onFileSelectionChanged = (files: FileContainer[] | FileContainer | undefined) => {
        const fileContainer = files as FileContainer | undefined;
        setSelectedFileContainer(fileContainer);
        setValue("fileName", fileContainer ? fileContainer.file.name : "", { shouldValidate: !!fileContainer });
    };

    const onImageFileSelectionChanged = (files: FileContainer[] | FileContainer | undefined) => {
        const fileContainer = files as FileContainer | undefined;

        if (fileContainer) {
            checkImageFileSize(fileContainer.file.size);
        }

        setSelectedImageFileContainer(fileContainer);
        setValue("imageFileName", fileContainer ? fileContainer.file.name : "", { shouldValidate: !!fileContainer });
        setValue("imageFile", fileContainer ? fileContainer : undefined, { shouldValidate: !!fileContainer });
    };

    const getFormattedFileSize = (bitSize: number) => {
        if (!bitSize) {
            return "N/A";
        }
        const kbSize = bitSize / 1024;
        if (kbSize < 1024) {
            return `${kbSize.toFixed(0)} KB`;
        }
        const mbSize = kbSize / 1024;
        return `${mbSize.toFixed(1)} MB`;
    };

    const checkImageFileSize = (bitSize: number) => {
        if (!bitSize) {
            return true;
        }
        if (bitSize / 1024 > 200) {
            setError("imageFile", { type: "manual", message: "uh oh!" });
            return false;
        } else {
            clearErrors("imageFile");
            return true;
        }
    };

    return (
        <Modal initialFocusRef={initialTitleRef} finalFocusRef={initialTitleRef} isOpen={show} onClose={handleClose}>
            <ModalOverlay />
            <ModalContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalHeader>{report ? "Edit " : "Add "} Report</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Flex gap="1.5rem" direction="column">
                            <FormControl isInvalid={errors.title ? true : undefined}>
                                <FormLabel>Title</FormLabel>
                                <Input
                                    placeholder="Fill in title here.."
                                    {...titleRest}
                                    ref={e => {
                                        titleRef(e);
                                        initialTitleRef.current = e;
                                    }}
                                />
                                <FormErrorMessage>{errors?.title?.message}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!errors?.description}>
                                <Flex>
                                    <FormLabel>Description</FormLabel>
                                </Flex>
                                <Textarea placeholder="Fill in report description here.." {...register("description")} maxLength={500} />
                                <FormErrorMessage>{errors?.description?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={errors.fileName ? true : undefined}>
                                <FormLabel>Report File</FormLabel>
                                {report && (
                                    <Flex flexDirection="row" border="solid 2px var(--chakra-colors-gray-200)" p="0.5rem" my="1rem" bg="gray.50" gap="0.5rem">
                                        <Box>
                                            <strong>Previous Report:</strong> {report?.title ? <>{report?.reportName}</> : <>N/A</>}
                                        </Box>
                                    </Flex>
                                )}
                                <Controller
                                    control={control}
                                    name="fileName"
                                    rules={{
                                        required: "Please select a report file (.pbix)",
                                    }}
                                    render={() => (
                                        <Flex flexDirection="row" gap="1rem" my="1rem">
                                            <UploadButton title="Select Report File" onFileSelectionChanged={onFileSelectionChanged} accept=".pbix" />
                                            {selectedFileContainer && (
                                                <Flex direction="column" gap=".25rem">
                                                    <Box>
                                                        <strong>Name:</strong> {selectedFileContainer.file.name}
                                                    </Box>
                                                    <Box>
                                                        <strong>Size:</strong> {getFormattedFileSize(selectedFileContainer.file.size)}
                                                    </Box>
                                                </Flex>
                                            )}
                                        </Flex>
                                    )}
                                ></Controller>
                                <FormErrorMessage>{errors?.fileName?.message}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={errors.imageFile ? true : undefined}>
                                <FormLabel>Image File</FormLabel>
                                {report && (
                                    <Flex flexDirection="row" border="solid 2px var(--chakra-colors-gray-200)" p="0.5rem" bg="gray.50" gap="0.5rem">
                                        <Box width="40%" borderRight="solid 2px var(--chakra-colors-gray-200)" p="0.5rem">
                                            {report?.imageFileName ? (
                                                <Image
                                                    src={"data:image/png;base64," + report.imageFile}
                                                    alt={report.imageFileName}
                                                    borderRadius="sm"
                                                    objectFit="cover"
                                                />
                                            ) : (
                                                <Icon as={IconPhotoOff} color="blue.200" objectFit="cover" strokeWidth="1" height="100%" width="auto" />
                                            )}
                                        </Box>
                                        <Box>
                                            <strong>Previous Image File:</strong> {report?.imageFileName ? <>{report?.imageFileName}</> : <>N/A</>}
                                        </Box>
                                    </Flex>
                                )}

                                <Controller
                                    control={control}
                                    name="imageFile"
                                    rules={{
                                        validate: value => {
                                            return checkImageFileSize(value?.file.size || 0);
                                        },
                                    }}
                                    render={() => (
                                        <Flex flexDirection="row" gap="1rem" my="1rem">
                                            <UploadButton
                                                title={report?.imageFileName ? "Change Image" : "Select Image"}
                                                onFileSelectionChanged={onImageFileSelectionChanged}
                                                accept=".jpg, .png"
                                            />
                                            {selectedImageFileContainer && (
                                                <Flex direction="column">
                                                    <Box>
                                                        <strong>Selected Image File:</strong> {selectedImageFileContainer.file.name}
                                                    </Box>
                                                    <Box>
                                                        <strong>Size:</strong> {getFormattedFileSize(selectedImageFileContainer.file.size)}
                                                    </Box>
                                                </Flex>
                                            )}
                                        </Flex>
                                    )}
                                ></Controller>
                                <FormErrorMessage>File is greater than 200KB.</FormErrorMessage>
                            </FormControl>

                            {/*{enableToggleDashboard && (*/}
                            {/*    <FormControl isInvalid={!!errors?.showOnDashboard} display="flex" justifyContent="flex-end" alignItems="center" gap="1rem">*/}
                            {/*        <FormLabel m={0}>Show in dashboard</FormLabel>*/}
                            {/*        <Switch {...register("showOnDashboard")} size={"lg"} colorScheme={"green"} />*/}
                            {/*        <FormErrorMessage>{errors?.showOnDashboard?.message}</FormErrorMessage>*/}
                            {/*    </FormControl>*/}
                            {/*)}*/}
                            {currentUser.isInternal && (
                                <FormControl isInvalid={!!errors?.isPublished} display="flex" justifyContent="flex-end" alignItems="center" gap="1rem">
                                    <FormLabel m={0}>Visible to Client</FormLabel>
                                    <Switch {...register("isPublished")} size={"lg"} colorScheme={"green"} />
                                    <FormErrorMessage>{errors?.isPublished?.message}</FormErrorMessage>
                                </FormControl>
                            )}
                        </Flex>
                    </ModalBody>

                    <ModalFooter mt="2rem" px="1rem">
                        <Flex width="100%" justify={report ? "space-between" : "flex-end"} align="center" gap="2rem">
                            {report && (
                                <Button
                                    type="button"
                                    colorScheme="red"
                                    variant="ghost"
                                    fontWeight="normal"
                                    mr=".5rem"
                                    isLoading={removeReportPending}
                                    onClick={() => handleRemoveReportClick(report)}
                                >
                                    Remove
                                </Button>
                            )}
                            <Box>
                                <Button
                                    type="submit"
                                    colorScheme="blue"
                                    mr=".5rem"
                                    isLoading={createReportPending || updateReportFilePending || uploadReportFilePending || uploadReportImageFilePending}
                                >
                                    Save
                                </Button>
                                <Button type="button" variant="ghost" onClick={handleClose}>
                                    Cancel
                                </Button>
                            </Box>
                        </Flex>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};

export default EditReportDialog;
